import { EntitySharing, EntityType, SharedAccountBadge, isShared, isSharedIncoming, isSharedOutgoing } from '@frontend/common';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';

export interface EntitySharingSectionProps {
    shared?: EntitySharing | null;
}

const SharedAccountChip = ({ badge }: { badge: SharedAccountBadge }) => (
    <PlatformObjectBadge
        id={badge.entity_id}
        entityType={badge.entity_type || EntityType.ACCOUNT}
        display={badge.name}
    />
);

const sharingCardClassName =
    'bg-background-surface outline-border-default inline-flex flex-col items-start justify-start gap-2 rounded-xl p-3 outline-1 outline-offset-[-1px]';

/**
 * Compact detail-modal cards for `shared.shared_from` / `shared.shared_with`.
 * Hidden when neither side is present.
 */
export const EntitySharingSection = ({ shared }: EntitySharingSectionProps) => {
    if (!isShared(shared)) {
        return null;
    }

    const from = isSharedIncoming(shared) ? shared?.shared_from : null;
    const withAccounts = isSharedOutgoing(shared) ? (shared?.shared_with ?? []) : [];

    return (
        <div
            data-testid='entity-sharing-section'
            className='flex w-full flex-col items-stretch gap-3 self-stretch md:flex-row md:items-start'>
            {from && (
                <div
                    data-testid='entity-sharing-from'
                    className={sharingCardClassName}>
                    <div className='text-foreground-default justify-start text-base font-semibold'>
                        <FormattedMessage
                            id='platform.components.entity-sharing-section.sharing-from-title'
                            description='Compact card title when an entity was shared with the current account'
                            defaultMessage='Sharing from'
                        />
                    </div>
                    <SharedAccountChip badge={from} />
                </div>
            )}
            {withAccounts.length > 0 && (
                <div
                    data-testid='entity-sharing-with'
                    className={sharingCardClassName}>
                    <div className='text-foreground-default justify-start text-base font-semibold'>
                        <FormattedMessage
                            id='platform.components.entity-sharing-section.sharing-with-title'
                            description='Compact card title when an entity is shared with other accounts'
                            defaultMessage='Sharing with'
                        />
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                        {withAccounts.map((badge) => (
                            <SharedAccountChip
                                key={badge.entity_id}
                                badge={badge}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
